import { Eye, EyeOff } from "lucide-react";
import { forwardRef, useState, type InputHTMLAttributes } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/cn";

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => (
  <input
    ref={ref}
    className={cn(
      // border-line-strong, not border-line: an input must read as an input,
      // and WCAG 1.4.11 wants 3:1 on the boundary.
      "h-control-md w-full rounded-sm border border-line-strong bg-surface px-3 text-md text-ink",
      "placeholder:text-ink-subtle disabled:bg-surface-sunken disabled:text-ink-disabled",
      "aria-[invalid=true]:border-danger",
      className,
    )}
    {...props}
  />
));
Input.displayName = "Input";

/**
 * A money field, with the currency inside the box.
 *
 * The rupee sign used to be passed as the field's `hint`, which renders BELOW
 * the control -- so seventeen forms across the product showed a lone "₹"
 * floating under an empty box, captioning nothing. It belongs against the
 * figure it qualifies, where the eye reads it as part of the amount.
 */
/**
 * Keep only what an amount can be: digits, one point, two decimals.
 *
 * `inputMode="decimal"` is a hint to a phone keyboard and nothing more -- on
 * a desktop keyboard the field took letters, several decimal points, and
 * pasted text, all of which reached the form as an unparseable amount.
 *
 * A negative sign is dropped rather than handled: every amount in this
 * product is positive, and direction is carried by the ledger entry.
 */
export function sanitiseAmount(raw: string): string {
  let value = raw.replace(/[^\d.]/g, "");
  const point = value.indexOf(".");
  if (point !== -1) {
    // Only the first point survives; "12.3.4" is a slip, not two numbers.
    value =
      value.slice(0, point + 1) + value.slice(point + 1).replace(/\./g, "");
  }
  const [whole, fraction] = value.split(".");
  if (fraction !== undefined) {
    // Paise, so two places. A third digit is a typo that would round away
    // silently on the way to the server.
    value = `${whole}.${fraction.slice(0, 2)}`;
  }
  return value;
}

export const MoneyInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(({ className, onChange, ...props }, ref) => (
  <div className="relative">
    <span
      className="tabular pointer-events-none absolute inset-y-0 left-3 grid place-items-center font-mono text-md text-ink-subtle"
      aria-hidden="true"
    >
      ₹
    </span>
    <Input
      ref={ref}
      inputMode="decimal"
      // Filtered here rather than at each of the dozen call sites, so every
      // money field in the product refuses the same things.
      onChange={(event) => {
        const cleaned = sanitiseAmount(event.target.value);
        if (cleaned !== event.target.value) {
          // Rewrite before forwarding: the caller owns the value, and the
          // DOM node must not keep a character the caller never sees.
          event.target.value = cleaned;
        }
        onChange?.(event);
      }}
      // Room for the sign, and tabular figures so a column of amounts lines
      // up the way it does everywhere else money is shown.
      className={cn("tabular pl-8 font-mono", className)}
      {...props}
    />
  </div>
));
MoneyInput.displayName = "MoneyInput";

/**
 * A password field you can check before you commit to it.
 *
 * Passwords here are handed over in person -- read off a paper slip at a
 * doorstep, typed on a phone by someone who may not use a keyboard often -- so
 * "did I type that right" is the ordinary case, not the suspicious one. Hiding
 * the value with no way to look at it turns one mistyped character into a
 * failed sign-in with nothing to learn from.
 *
 * The toggle is a real button: reachable by keyboard, labelled for a screen
 * reader, and `type="button"` so pressing Enter in the field still submits the
 * form rather than revealing the password.
 */
export const PasswordInput = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, "type">
>(({ className, ...props }, ref) => {
  const [shown, setShown] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="relative">
      <Input
        ref={ref}
        type={shown ? "text" : "password"}
        // Room for the button, so a long password never runs under it.
        className={cn("pr-10", className)}
        {...props}
      />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        aria-pressed={shown}
        aria-label={t(shown ? "action.hidePassword" : "action.showPassword")}
        title={t(shown ? "action.hidePassword" : "action.showPassword")}
        className="absolute inset-y-0 right-0 grid w-10 place-items-center rounded-r-sm text-ink-subtle transition-colors hover:text-ink"
      >
        {shown ? (
          <EyeOff className="size-4" aria-hidden="true" />
        ) : (
          <Eye className="size-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
});
PasswordInput.displayName = "PasswordInput";
